import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthContext } from "./authentication/AuthContext";
import ProtectedRoute from "./authentication/ProtectedRoute";

// Layout Components
import LeftSidebarMenu from "./components/Layout/LeftSidebarMenu";
import Footer from "./components/Layout/Footer";
import TopNavbar from "./components/Layout/TopNavbar";
import ScrollToTop from "./components/Layout/ScrollToTop";
import SessionExpiryModal from "./components/SessionExpiryModal";

// Pages
import Dashboard from "./pages/admin-dashboard/dashboard";
import BillingHistory from "./pages/admin-dashboard/billing-history";
import Revenue from "./pages/admin-dashboard/revenue";

import ChatTranscripts from "./pages/admin-dashboard/chat-transcripts";
import ClientManagement from "./pages/admin-dashboard/client-management";
import Notifications from "./pages/admin-dashboard/notifications";
import UserManagement from "./pages/admin-dashboard/user-management";
import CreateClient from "./pages/admin-dashboard/client-management/create-client";
import AddingNewUser from "./pages/admin-dashboard/user-management/add-user";
import EditUserPage from "./pages/admin-dashboard/user-management/edit-user";
import EditProfilePage from "./pages/admin-dashboard/edit-profile";
import ChatWindow from "./pages/admin-dashboard/chat-transcripts/chat-window";
import ClientStores from "./pages/admin-dashboard/client-store";
import Products from "./pages/admin-dashboard/products";
import ClientFeedBack from "./pages/admin-dashboard/client-feedback";
import ChatFeedbackDetail from "./pages/admin-dashboard/client-feedback/chat-feedback-detail";
import ReportProblem from "./pages/admin-dashboard/report-problem";
import ViewStorePage from "./pages/admin-dashboard/client-store/view-store";
import SessionChatTranscript from "./components/AdminDashboard/ReportProblems/SessionChatTranscript";

// Batch Jobs
import BatchJobs from "./pages/admin-dashboard/batch-jobs/BatchJobs";
import JobLogs from "./pages/admin-dashboard/batch-jobs/JobLogs";


// Authentication Pages
import SignIn from "./pages/authentication/SignIn";
import ForgotPassword from "./pages/authentication/ForgotPassword";
import ResetPassword from "./pages/authentication/ResetPassword";

// Analytics Layout and Pages

import AnalyticsLayout from "./components/AdminDashboard/Analytics/AnalyticsLayout";
import BotEffectiveness from "./components/AdminDashboard/Analytics/BotEffectiveness";
import BotEffectivenessDetails from "./components/AdminDashboard/Analytics/BotEffectivenessDetails";
import BotEffectivenessTablePage from "./components/AdminDashboard/Analytics/BotEffectivenessTablePage";
import FallbackDetails from "./components/AdminDashboard/Analytics/FallbackDetails";

import TrainingAndPerformance from "./components/AdminDashboard/Analytics/TrainingAndPerformance";
import TrainingPerformanceDetailsPage from "./components/AdminDashboard/Analytics/TrainingPerformanceDetailsPage";

import DataHealthAndReliability from "./components/AdminDashboard/Analytics/DataHealthAndReliability";
import DataHealthviewdetails from "./components/AdminDashboard/Analytics/DataHealthviewdetails";

import FeaturePerformance from "./components/AdminDashboard/Analytics/FeaturePerformance";
import FeatureDropoffDetails from "./components/AdminDashboard/Analytics/FeatureDropoffDetails";
import FeatureUsageDetails from "./components/AdminDashboard/Analytics/FeatureUsageDetails";
import ProductDetails from "./components/AdminDashboard/Products/product_details";

// import ClientFeedbackList from "./components/AdminDashboard/ClientFeedback";

import ProblemReportsList from "./components/AdminDashboard/ReportProblems";

const App = () => {
  const [active, setActive] = useState(false);
  const location = useLocation();
  const { token, loading } = useContext(AuthContext);

  // Check if current page is an auth page
  const isAuthPage = location.pathname.startsWith("/authentication");

  // ✅ GLOBAL AXIOS INTERCEPTOR: Automatically attach token to all requests
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token]);

  // Redirect to login if not authenticated and not on auth page
  if (!loading && !token && !isAuthPage) {
    return <Navigate to="/authentication/sign-in" replace />;
  }

  return (
    <div className={`main-wrapper-content ${active ? "active" : ""}`}>
      <SessionExpiryModal />
      {!isAuthPage && (
        <>
          <TopNavbar toggleActive={() => setActive(!active)} />
          <LeftSidebarMenu toggleActive={() => setActive(!active)} />
        </>

      )}

      <div
        className="main-content"
        style={{
          minHeight: "calc(100vh - 100px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ScrollToTop />

        <div style={{ flex: 1 }}>
          <Routes>
            {/* Authentication Routes */}
            <Route path="/authentication/sign-in" element={<SignIn />} />

            {/* <Route path="/authentication/forgot-password" element={<ForgotPassword />} />
            <Route path="/authentication/reset-password" element={<ResetPassword />} /> */}

            {/* Dashboard */}
            <Route path="/" element={<Dashboard />} />

            {/* Revenue */}
            <Route path="/clients-order" element={<Revenue />} />
            <Route path="/revenue" element={<BillingHistory />} />


            {/* Batch Jobs */}
            <Route path="/batch-jobs" element={<BatchJobs />} />
            <Route path="/job-logs" element={<JobLogs />} />


            {/* Chat */}
            <Route path="/chat-transcripts" element={<ChatTranscripts />} />
            <Route
              path="/chat-transcripts/chats/:customer_id"
              element={<ChatWindow />}
            />

            {/* Client Management */}
            <Route path="/client-management" element={<ClientManagement />} />
            <Route
              path="/client-management/create-client"
              element={<CreateClient />}
            />
            <Route
              path="/client-management/edit-client/:client_id"
              element={<CreateClient />}
            />

            {/* User Management - Superadmin Only */}
            <Route
              path="/user-management"
              element={
                <ProtectedRoute allowedRoles={["superadmin"]}>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-management/add-user"
              element={
                <ProtectedRoute allowedRoles={["superadmin"]}>
                  <AddingNewUser />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-management/:admin_user_id/edit-user"
              element={
                <ProtectedRoute allowedRoles={["superadmin"]}>
                  <EditUserPage />
                </ProtectedRoute>
              }
            />

            {/* Edit Profile */}
            <Route
              path="/edit-profile"
              element={<EditProfilePage />}
            />

            {/* Notifications */}
            <Route path="/notifications" element={<Notifications />} />

            {/* Client Stores */}
            <Route path="/client-stores" element={<ClientStores />} />
            <Route path="/store_id/:store_id" element={<ViewStorePage />} />

            {/* ---------------------- ANALYTICS ROUTES ---------------------- */}
            <Route path="/analytics" element={<AnalyticsLayout />}>
              {/* Main */}
              <Route path="/analytics" element={<AnalyticsLayout />}></Route>
              <Route index element={<BotEffectiveness />} />
              <Route path="bot-effectiveness" element={<BotEffectiveness />} />
              <Route
                path="bot-effectiveness/details"
                element={<BotEffectivenessDetails />}
              />
              <Route
                path="bot-effectiveness/table"
                element={<BotEffectivenessTablePage />}
              />
              <Route
                path="bot-effectiveness/fallback-details"
                element={<FallbackDetails />}
              />

              {/* Training */}
              <Route
                path="training-performance"
                element={<TrainingAndPerformance />}
              />
              <Route
                path="training-performance/details"
                element={<TrainingPerformanceDetailsPage />}
              />

              {/* Data Health */}
              <Route
                path="data-health-reliability"
                element={<DataHealthAndReliability />}
              />
              <Route
                path="data-health/details"
                element={<DataHealthviewdetails />}
              />

              {/* Feature Performance */}
              <Route
                path="feature-performance"
                element={<FeaturePerformance />}
              />
              <Route
                path="feature-performance/line-details"
                element={<FeatureDropoffDetails />}
              />
              <Route
                path="feature-performance/donut-details"
                element={<FeatureUsageDetails />}
              />
            </Route>
            {/* --------------------------------------------------------------- */}

            {/* Products */}
            <Route path="/products" element={<Products />} />
            {/* <Route
              path="/product/details/:product_code"
              element={<ProductDetails />}
            /> */}
            <Route path="/product/details/:product_id" element={<ProductDetails />} />

            {/* Feedback */}
            <Route path="/client-feedback" element={<ClientFeedBack />} />
            <Route path="/chat-feedback/:chat_feedback_id" element={<ChatFeedbackDetail />} />

            {/* Support */}
            <Route path="/chat-problems-report" element={<ReportProblem />} />
            <Route path="/problem-reports/:feedback_id" element={<SessionChatTranscript />} />
          </Routes>
        </div>

        {!isAuthPage && <Footer />}
      </div>
    </div>
  );
};

export default App;
